import { SiteServiceClient } from '../../grpc/SiteServiceClientPb';
import { GRPC_SITE_PORT } from '../../constants';
import {
  LastTimeSlotResponse,
  OperatingAreaDetails,
  OperatingAreaScheduleRequest,
  OperatingAreasRequest,
  OperatingAreasResponse,
  RemoveRequest,
  SetAlternateScheduleRequest,
  SetRegularScheduleRequest,
} from '../../grpc/site_pb';
import { OperatingAreaRequest, Empty, LocalDateRange } from '../../grpc/shared_pb';
import Auth from '../../auth/abstract-auth';
import { CONSOLE_ERROR_MSG, requestMetadata } from '../util';
import { toAlternateSchedule, ScheduleBlock, AlternateSchedule, toGrpcScheduleBlock, toGrpcMaterializedScheduleBlock, RegularSchedule, toRegularSchedule, toMaterializedSchedule, MaterializedSchedule } from '@optimusride/client-library/@types/grpc/schedule';

export default class SiteServiceConsumer {
  private siteServiceClient: SiteServiceClient;
  private auth: Auth;
  private oriApiKey: undefined | string;

  constructor(auth: Auth, hostname: string, oriApiKey?: undefined | string) {
    this.auth = auth;
    this.oriApiKey = oriApiKey;
    this.siteServiceClient = new SiteServiceClient(`${hostname}:${GRPC_SITE_PORT}`, null, null);
  }

  public getOperatingAreaDetails = (operatingAreaId: number): Promise<OperatingAreaDetails> => {
    const request = new OperatingAreasRequest();
    request.setOperatingAreaIdsList([operatingAreaId]);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getOperatingAreaDetails(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const operatingAreas = response.getOperatingAreas();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasOperatingAreas() && operatingAreas) {
              resolve(operatingAreas.getOperatingAreasList()[0]);
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
      });
  }

  public getAllOperatingAreaDetails = (): Promise<OperatingAreasResponse> => 
    new Promise(async (resolve, reject) => {
      this.siteServiceClient.getAllOperatingAreaDetails(new Empty(), await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          const operatingAreas = response.getOperatingAreas();
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasOperatingAreas() && operatingAreas) {
            resolve(operatingAreas);
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  

  public getOperatingAreas = (operatingAreaIds: number[]): Promise<OperatingAreasResponse> => {
    const request = new OperatingAreasRequest();
    request.setOperatingAreaIdsList(operatingAreaIds);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getOperatingAreaDetails(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          const operatingAreas = response.getOperatingAreas();
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasOperatingAreas() && operatingAreas) {
            resolve(operatingAreas);
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public setRegularSchedule = (operatingAreaId: number, dayOfWeek: number[], scheduleBlocks: ScheduleBlock[], startDate: string): Promise<Empty | undefined> => {
    const request = new SetRegularScheduleRequest();
    request.setStartDate(startDate);
    request.setOperatingAreaId(operatingAreaId);
    request.setDayOfWeekList(dayOfWeek);
    request.setScheduleBlocksList(scheduleBlocks.map(toGrpcScheduleBlock));
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.setRegularSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()){
            resolve(response.getEmpty());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public removeAlternateSchedule = (operatingAreaId: number, date: string): Promise<Empty | undefined> => {
    const request = new RemoveRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setDate(date);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.removeAlternateSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()){
            resolve(response.getEmpty());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public setCancellation = (operatingAreaId: number, date: string): Promise<Empty | undefined> => {
    const request = new RemoveRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setDate(date);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.setCancellation(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()){
            resolve(response.getEmpty());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public removeCancellation = (operatingAreaId: number, date: string): Promise<Empty | undefined> => {
    const request = new RemoveRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setDate(date);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.removeCancellation(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()){
            resolve(response.getEmpty());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public setAlternateSchedule = (operatingAreaId: number, date: string, scheduleBlocks: ScheduleBlock[], operatingArea: OperatingAreaDetails): Promise<Empty | undefined> => {
    const request = new SetAlternateScheduleRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setDate(date);
    request.setScheduleBlocksList(scheduleBlocks.map(b => toGrpcMaterializedScheduleBlock(b, date, operatingArea.getTimeZone())));
    console.log(request.getScheduleBlocksList())
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.setAlternateSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()){
            resolve(response.getEmpty());
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });
  }

  public getAlternateSchedule = (operatingAreaId: number, range: LocalDateRange): Promise<AlternateSchedule[]> => {
    const request = new OperatingAreaScheduleRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setRange(range);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getAlternateSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const schedule = response.getAlternateSchedule();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasAlternateSchedule() && schedule) {
              resolve(schedule.getAlternateSchedulesList().map(toAlternateSchedule));
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });
  }

  public getSchedule = (operatingAreaId: number, range: LocalDateRange): Promise<MaterializedSchedule[]> => {
    const request = new OperatingAreaScheduleRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setRange(range);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const schedule = response.getMaterializedSchedule();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasMaterializedSchedule() && schedule) {
              resolve(schedule.getSchedulesList().map(toMaterializedSchedule));
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });
  }

  public getRegularSchedule = (operatingAreaId: number): Promise<RegularSchedule> => {
    const request = new OperatingAreaRequest();
    request.setId(operatingAreaId);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getRegularSchedule(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const schedule = response.getRegularSchedule();
            if (response.hasError()) {
              reject(response.getError() && schedule);
            } else if (response.hasRegularSchedule()) {
              resolve(toRegularSchedule(schedule));
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });
  }

  public getLastTimeSlot = (operatingAreaId: number, range: LocalDateRange): Promise<LastTimeSlotResponse> => {
    const request = new OperatingAreaScheduleRequest();
    request.setOperatingAreaId(operatingAreaId);
    request.setRange(range);
    return new Promise(async (resolve, reject) => {
      this.siteServiceClient.getLastTimeSlot(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
          if (!err) {
            const lastTimeSlot = response.getLastTimeSlot();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasLastTimeSlot() && lastTimeSlot) {
              resolve(lastTimeSlot);
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });
  }
}
